import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import CartaoParticipacao from './CartaoParticipacao';
import { novaParticipacao, rolarParaPrimeiroErro, sugerirCodigo } from '../utils';
import type { FormaParticipar, Grupo } from '../types';

export interface PassoParticipacoesHandle {
  validar: () => boolean;
}

function diaSeguinte(data: string): string {
  const d = new Date(`${data}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

// Sugestão (nunca gravada sozinha): o lote seguinte abre no dia depois de o
// anterior fechar, quando os dois ainda dividem a mesma data de abertura.
function sugestaoDeAbertura(lista: FormaParticipar[], indice: number) {
  const atual = lista[indice];
  const anterior = lista[indice - 1];
  if (!atual || !anterior) return null;
  if (!/lote/i.test(atual.nome)) return null;
  if (!anterior.inscricoesFechamData || !atual.inscricoesAbremData) return null;
  if (atual.inscricoesAbremData !== anterior.inscricoesAbremData) return null;
  const sugerida = diaSeguinte(anterior.inscricoesFechamData);
  if (!sugerida) return null;
  const [sugeridaAno, sugeridaMes, sugeridaDia] = sugerida.split('-');
  if (!sugeridaAno) return null;
  return {
    data: sugerida,
    aviso: `O ${atual.nome} passa a abrir em ${sugeridaDia}/${sugeridaMes}. Confira antes de salvar.`,
  };
}

interface PassoParticipacoesProps {
  participacoes: FormaParticipar[];
  slug: string;
  ehIngresso: boolean;
  onChange: (participacoes: FormaParticipar[]) => void;
  /** Liga/desliga a validação presencial numa modalidade já salva. */
  aoMudarPresencial?: (modalidadeId: number, ligado: boolean) => Promise<void>;
}

function PassoParticipacoes(
  { participacoes, slug, ehIngresso, onChange, aoMudarPresencial }: PassoParticipacoesProps,
  ref: React.Ref<PassoParticipacoesHandle>,
) {
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const [erros, setErros] = useState<Record<string, string>>({});
  const [ajudaAberta, setAjudaAberta] = useState(false);

  // A dica "Tipos de inscrição" pede para abrir este exemplo (em vez de navegar).
  useEffect(() => {
    function aoAcao(e: Event) {
      if ((e as CustomEvent).detail === 'exemplo') setAjudaAberta(true);
    }
    window.addEventListener('seliga:dica', aoAcao);
    return () => window.removeEventListener('seliga:dica', aoAcao);
  }, []);

  useImperativeHandle(ref, () => ({
    validar() {
      const novos: Record<string, string> = {};
      if (participacoes.length === 0) {
        novos.vazio = ehIngresso
          ? 'Crie pelo menos um ingresso.'
          : 'Crie pelo menos um tipo de inscrição.';
      }
      participacoes.forEach((p, i) => {
        if (!p.nome.trim()) {
          novos[`${i}:nome`] = ehIngresso
            ? 'Digite o nome deste ingresso.'
            : 'Digite o nome deste tipo de inscrição.';
        }
        if (!p.publica && !p.codigoAcesso.trim()) {
          novos[`${i}:codigoAcesso`] = 'Digite um código de acesso.';
        }
        if (p.temLimite) {
          if (p.vagas === null || p.vagas < 1) {
            novos[`${i}:vagas`] = 'Digite quantas vagas.';
          } else if (p.vagas < p.inscritos) {
            novos[
              `${i}:vagas`
            ] = `Já existem ${p.inscritos} inscritos nesta opção. O limite não pode ser menor que isso.`;
          }
        }
        if (p.dividirGrupos) {
          if (p.grupos.length === 0) novos[`${i}:grupos`] = 'Adicione pelo menos um grupo.';
          p.grupos.forEach((g, gi) => {
            if (!g.nome.trim()) novos[`${i}:g${gi}:nome`] = 'Digite o nome do grupo.';
          });
        }
      });
      setErros(novos);
      if (Object.keys(novos).length > 0) {
        rolarParaPrimeiroErro();
        return false;
      }
      return true;
    },
  }));

  function mudar(indice: number, patch: Partial<FormaParticipar>) {
    const novo = participacoes.map((p, i) => {
      if (i !== indice) return p;
      let atualizada = { ...p, ...patch };

      // Ao escolher "só quem receber o link", sugere um código automaticamente.
      if (patch.publica === false && !atualizada.codigoAcesso) {
        atualizada = {
          ...atualizada,
          codigoAcesso: sugerirCodigo(atualizada.nome || 'ACESSO'),
        };
      }

      // Ao ativar a divisão, sugere Masculino e Feminino.
      if (patch.dividirGrupos === true && atualizada.grupos.length === 0) {
        atualizada = {
          ...atualizada,
          grupos: [
            { id: null, nome: 'Masculino', vagas: null, valorCentavos: 0 },
            { id: null, nome: 'Feminino', vagas: null, valorCentavos: 0 },
          ],
        };
      }

      // Gratuito: zera o valor da inscrição e de todos os grupos.
      if (patch.valorModo === 'gratuito') {
        atualizada = {
          ...atualizada,
          valorCentavos: 0,
          grupos: atualizada.grupos.map((g) => ({ ...g, valorCentavos: 0 })),
        };
      }

      return atualizada;
    });

    onChange(novo);
    if (erros[`${indice}:nome`] && patch.nome) {
      setErros((e) => ({ ...e, [`${indice}:nome`]: '' }));
    }
    if (erros[`${indice}:codigoAcesso`] && patch.codigoAcesso) {
      setErros((e) => ({ ...e, [`${indice}:codigoAcesso`]: '' }));
    }
    if (erros[`${indice}:vagas`] && patch.vagas !== undefined) {
      setErros((e) => ({ ...e, [`${indice}:vagas`]: '' }));
    }
  }

  function mudarGrupo(indice: number, gi: number, patch: Partial<Grupo>) {
    const novo = participacoes.map((p, i) => {
      if (i !== indice) return p;
      const grupos = p.grupos.map((g, j) => (j === gi ? { ...g, ...patch } : g));
      return { ...p, grupos };
    });
    onChange(novo);
    if (erros[`${indice}:g${gi}:nome`] && patch.nome) {
      setErros((e) => ({ ...e, [`${indice}:g${gi}:nome`]: '' }));
    }
  }

  function adicionarGrupo(indice: number) {
    const novo = participacoes.map((p, i) =>
      i === indice
        ? { ...p, grupos: [...p.grupos, { id: null, nome: '', vagas: null, valorCentavos: 0 }] }
        : p,
    );
    onChange(novo);
  }

  function removerGrupo(indice: number, gi: number) {
    const novo = participacoes.map((p, i) =>
      i === indice ? { ...p, grupos: p.grupos.filter((_, j) => j !== gi) } : p,
    );
    onChange(novo);
  }

  function alternar(indice: number) {
    setExpandidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(indice)) novo.delete(indice);
      else novo.add(indice);
      return novo;
    });
  }

  function remover(indice: number) {
    onChange(participacoes.filter((_, i) => i !== indice));
  }

  function adicionar() {
    onChange([...participacoes, novaParticipacao()]);
  }

  const erroGrupoNome = (indice: number) => (gi: number) => erros[`${indice}:g${gi}:nome`];

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-normal text-foreground-950">
            {ehIngresso ? 'Quais ingressos você vende' : 'Como as pessoas se inscrevem'}
          </h2>
          <button
            type="button"
            onClick={() => setAjudaAberta(true)}
            aria-label="O que é um tipo de inscrição?"
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-secondary-300 text-secondary-500 transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            <i className="ri-question-line text-sm" />
          </button>
        </div>
        <p className="mt-1 text-sm text-secondary-500">
          {ehIngresso
            ? 'Crie um ingresso para cada lote ou tipo. Cada um tem preço, quantidade e período de venda.'
            : 'Crie um tipo de inscrição para cada grupo que vai participar. Cada um tem seu preço, suas vagas e suas próprias datas de inscrição.'}
        </p>
        <p className="mt-2 text-xs text-secondary-500">
          São sugestões. Apague as que não servem.
        </p>
      </div>

      {erros.vazio && (
        <p
          className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700"
          role="alert"
          data-erro="true"
        >
          {erros.vazio}
        </p>
      )}

      {participacoes.map((p, i) => {
        const sugestao = ehIngresso ? sugestaoDeAbertura(participacoes, i) : null;
        return (
          <CartaoParticipacao
            key={i}
            participacao={p}
            slug={slug}
            ehIngresso={ehIngresso}
            indice={i}
            expandido={expandidos.has(i)}
            erroNome={erros[`${i}:nome`]}
            erroCodigo={erros[`${i}:codigoAcesso`]}
            erroVagas={erros[`${i}:vagas`]}
            erroGrupos={erros[`${i}:grupos`]}
            erroGrupoNome={erroGrupoNome(i)}
            aoAlternar={() => alternar(i)}
            aoMudar={(patch) => mudar(i, patch)}
            aoMudarGrupo={(gi, patch) => mudarGrupo(i, gi, patch)}
            aoAdicionarGrupo={() => adicionarGrupo(i)}
            aoRemoverGrupo={(gi) => removerGrupo(i, gi)}
            aoRemover={() => remover(i)}
            aoMudarPresencial={aoMudarPresencial}
            sugestaoAbertura={sugestao}
            aoAplicarSugestao={
              sugestao
                ? () => mudar(i, { inscricoesAbremData: sugestao.data, inscricoesAbremHora: '00:00' })
                : undefined
            }
          />
        );
      })}

      <button
        type="button"
        onClick={adicionar}
        className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border-2 border-dashed border-secondary-300 bg-white px-4 py-4 text-[15px] font-medium text-primary-600 transition-colors hover:border-primary-400 hover:bg-background-100"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className="ri-add-line" />
        </span>
        {ehIngresso ? 'Criar outro ingresso' : 'Criar outro tipo de inscrição'}
      </button>

      {ajudaAberta && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setAjudaAberta(false)}
        >
          <div
            className="w-full max-w-[500px] overflow-hidden rounded-lg border border-secondary-200 bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-secondary-100 px-5 py-4">
              <h3 className="text-base font-medium text-foreground-950">
                O que é um tipo de inscrição?
              </h3>
              <button
                type="button"
                onClick={() => setAjudaAberta(false)}
                aria-label="Fechar"
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4 text-sm text-foreground-800">
              <p>
                É cada grupo de pessoas que entra no seu evento de um jeito diferente — pagando
                outro valor, ocupando outra vaga, ou se inscrevendo em outra data.
              </p>

              <div>
                <p className="font-medium text-foreground-950">Num acampamento:</p>
                <div className="mt-1 space-y-1 text-secondary-600">
                  <p>Campista · R$ 200,00 · 120 vagas</p>
                  <p>Equipe · R$ 150,00 · 40 vagas</p>
                </div>
              </div>

              <div>
                <p className="font-medium text-foreground-950">Num encontro de casais:</p>
                <div className="mt-1 space-y-1 text-secondary-600">
                  <p>Casal · R$ 500,00 · 25 casais</p>
                  <p>Equipe · R$ 250,00 · 20 vagas</p>
                </div>
              </div>

              <div>
                <p className="font-medium text-foreground-950">Num show:</p>
                <div className="mt-1 space-y-1 text-secondary-600">
                  <p>Inteira · R$ 40,00</p>
                  <p>Meia-entrada · R$ 20,00</p>
                </div>
              </div>

              <p>
                As datas podem ser diferentes em cada um. É comum a equipe se inscrever um mês
                antes dos campistas.
              </p>

              <p>Se todo mundo entra do mesmo jeito, crie um só.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default forwardRef(PassoParticipacoes);